/**
 * Shopify standard storefront actions integration.
 * Listens for auto-emitted shopify:cart:* events and refreshes the theme cart UI.
 * See https://shopify.dev/docs/storefronts/themes/best-practices/standard-actions
 */

import { emitConfirmedCartDelta } from './events.js';

let standardActionRefreshPromise = Promise.resolve();
let standardActionMutationPromise = Promise.resolve();

// Standard events carry only id/quantity/cost. Enrich metadata by exact line ID,
// but retain the quantities from the mutation result, not a later cart fetch.
async function enrichActionCart(summary, fallback) {
  if (summary === null) return { lines: { edges: [] } };
  if (!summary) return null;
  if (summary.lines?.edges) return summary;
  if (!Array.isArray(summary.lines)) return null;
  let metadata = fallback;
  try {
    const { fetchServerCart } = await import('./graphql.js');
    metadata = (await fetchServerCart(summary.id)) || fallback;
  } catch {
    // Existing line metadata remains usable for confirmed decreases/removals.
  }
  const nodes = [...(metadata?.lines?.edges || []).map(({ node }) => node)];
  if (
    (summary.lines || []).some(
      (line) => !nodes.some((node) => node.id === line.id && node.merchandise)
    )
  ) {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Native cart metadata unavailable');
      const ajax = await response.json();
      const token = (id) =>
        String(id || '')
          .replace('gid://shopify/Cart/', '')
          .split('?')[0];
      // Never borrow metadata from the unrelated native cart for an explicit
      // Storefront cart. Native line keys, not merchandise-only matches, join it.
      if (ajax.token && token(ajax.token) === token(summary.id)) {
        for (const item of ajax.items || []) {
          nodes.push({
            id: item.key,
            attributes: Object.entries(item.properties || {}).map(([key, value]) => ({
              key,
              value,
            })),
            sellingPlanAllocation: item.selling_plan_allocation
              ? {
                  sellingPlan: { id: item.selling_plan_allocation.selling_plan?.id },
                }
              : undefined,
            merchandise: {
              id: `gid://shopify/ProductVariant/${item.variant_id}`,
              sku: item.sku,
              title: item.variant_title,
              price: {
                amount: (item.final_price ?? item.price) / 100,
                currencyCode: ajax.currency,
              },
              product: {
                id: item.product_id ? `gid://shopify/Product/${item.product_id}` : undefined,
                title: item.product_title,
                productType: item.product_type,
              },
            },
          });
        }
      }
    } catch {
      // Missing metadata suppresses only the affected item's event.
    }
  }
  return {
    ...summary,
    lines: {
      edges: (summary.lines || []).map((line) => ({
        node: { ...nodes.find((node) => node.id === line.id && node.merchandise), ...line },
      })),
    },
  };
}

/** Installed as updateCart.configure.handler by the early Liquid configuration. */
export function trackStandardCartMutation(defaultHandler, payload = {}) {
  const operation = standardActionMutationPromise.then(() =>
    trackMutation(defaultHandler, payload)
  );
  standardActionMutationPromise = operation.catch(() => {});
  return operation;
}

async function trackMutation(defaultHandler, payload) {
  let before = null;
  try {
    const getCart = window.Shopify?.actions?.getCart;
    if (typeof getCart === 'function') {
      const response = await getCart(payload.cartId ? { cartId: payload.cartId } : {});
      if (response && 'cart' in response) before = await enrichActionCart(response.cart);
    } else if (payload.cartId) {
      const { fetchServerCart } = await import('./graphql.js');
      before = await fetchServerCart(payload.cartId);
    }
  } catch {
    // Missing baseline means unknown, not an empty cart. Still perform the write.
  }
  const result = await defaultHandler();
  try {
    if (before && result?.cart) {
      const after = await enrichActionCart(result.cart, before);
      emitConfirmedCartDelta(before, after);
    }
  } catch {
    // Analytics/metadata failures cannot reject a successful mutation.
  }
  return result;
}

function getCartErrorMessage(event) {
  // Shopify dispatches CustomEvents, so the payload lives on event.detail.
  const detail = event?.detail || event;
  return detail?.error || detail?.message || 'Cart update failed';
}

function notifyCartRefreshError(error) {
  const message = error?.message || 'Unable to refresh cart';
  if (typeof window.showSimpleNotification === 'function') {
    window.showSimpleNotification(message, 'error');
  }
}

async function refreshAfterStandardAction(actionCart) {
  if (typeof window.__loadCartModule === 'function') {
    await window.__loadCartModule('standard-action');
  }

  if (typeof window.refreshCartFromServer === 'function') {
    await window.refreshCartFromServer(actionCart);
    return;
  }

  const cart = await window.getCart?.();
  if (cart && typeof window.renderCart === 'function') {
    await window.renderCart(cart);
  }
}

export function waitForStandardActionRefresh() {
  return standardActionRefreshPromise;
}

function notifyCartUserErrors(result) {
  // updateCart resolves (does not reject) when it returns userErrors/warnings.
  // userErrors mean the mutation failed; warnings mean it succeeded with a caveat.
  if (typeof window.showSimpleNotification !== 'function') return;

  const userErrorMessage = result?.userErrors?.[0]?.message;
  if (userErrorMessage) {
    window.showSimpleNotification(userErrorMessage, 'error');
    return;
  }

  const warningMessage = result?.warnings?.[0]?.message;
  if (warningMessage) {
    window.showSimpleNotification(warningMessage, 'info');
  }
}

async function handleResolvedCartMutation(result) {
  notifyCartUserErrors(result);
  // Always sync from server so openCart does not render stale local state after
  // failed or partial mutations that resolve with userErrors.
  await refreshAfterStandardAction(result?.cart);
}

function handleCartMutationFailure(error) {
  // Notify but do NOT re-throw: the tracked tail must settle resolved so that a
  // single failed refresh cannot leave waitForStandardActionRefresh (and the
  // openCart handler that awaits it) stuck on a permanently rejected promise.
  notifyCartRefreshError(error);
}

// Mirrors QUEUE_ACTION_SOURCE in queue.js (not imported: this module loads early).
const QUEUE_ACTION_SOURCE = 'ippodo-cart-queue';

function handleCartMutationEvent(event) {
  if (!event.promise) return;
  // The theme's own queue already reconciles its writes; refreshing here would
  // abort its in-flight batch.
  if (event.detail?.source === QUEUE_ACTION_SOURCE) return;
  // Serialize refreshes; handleCartMutationFailure swallows errors so the chain
  // always recovers for subsequent mutations.
  standardActionRefreshPromise = standardActionRefreshPromise
    .then(() => event.promise)
    .then(handleResolvedCartMutation)
    .catch(handleCartMutationFailure);
}

export function setupStandardActionListeners() {
  if (window.__standardActionListenersRegistered) return;
  window.__standardActionListenersRegistered = true;
  window.trackStandardCartMutation = trackStandardCartMutation;

  document.addEventListener('shopify:cart:lines-update', handleCartMutationEvent);

  document.addEventListener('shopify:cart:note-update', handleCartMutationEvent);

  document.addEventListener('shopify:cart:discount-update', handleCartMutationEvent);

  document.addEventListener('shopify:cart:error', (event) => {
    const message = getCartErrorMessage(event);
    if (typeof window.showSimpleNotification === 'function') {
      window.showSimpleNotification(message, 'error');
    }
  });
}
